import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"

import { ReactComponent as UploadArrowIcon } from "@common_assets/svg/uploadArrow.svg"
import { Box, Button, Divider, Stack, Typography } from "@mui/material"
import {
  useQueryExportReportById,
  useQueryExportReportDownload,
} from "api/reactQuery/queries/export"
import { ExportStatus, ExportType } from "api/reactQuery/queries/export.types"
import Snackbar from "components/common/snackbar/snackbar"
import { useSnackbar } from "notistack"
import { useTranslation } from "react-i18next"
import { EXPORT_TASKS } from "utils/exportTasks"

interface ExportTask {
  id: number
  filename: string
  created_at: string
  status: ExportStatus
  download_url?: string | null
  export_type: ExportType
}

interface ExportReadySnackbarContentProps {
  title: string
  onDownloadClick: () => void
  snackbarId: string | number
}

const ExportReadySnackbarContent: FC<ExportReadySnackbarContentProps> = ({
  title,
  onDownloadClick,
  snackbarId,
}) => {
  const { closeSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const handleDownloadClick = () => {
    onDownloadClick()
    closeSnackbar(snackbarId)
  }

  return (
    <Stack direction="column" spacing={1}>
      <Typography variant="body2" sx={{ fontWeight: "normal" }}>
        {title}
      </Typography>
      <Divider />
      <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
        <Button
          variant="text"
          size="small"
          onClick={handleDownloadClick}
          startIcon={<UploadArrowIcon width={12} height={12} />}
          sx={{
            textTransform: "none",
            color: "inherit",
            padding: 0,
            minWidth: "auto",
            fontSize: "0.75rem",
            "&:hover": {
              backgroundColor: "transparent",
            },
          }}
        >
          {t("allTransactions.downloadFile")}
        </Button>
      </Box>
    </Stack>
  )
}

const getAllExportTasks = (): ExportTask[] => {
  const jsonSessionStorage = sessionStorage.getItem(EXPORT_TASKS)
  if (!jsonSessionStorage) return []

  try {
    const res: Record<string, ExportTask[]> = JSON.parse(jsonSessionStorage)
    const allTasks: ExportTask[] = []
    Object.keys(res).forEach((exportType) => {
      res[exportType].forEach((task) => {
        allTasks.push({ ...task, export_type: exportType as ExportType })
      })
    })
    return allTasks
  } catch {
    return []
  }
}

const updateTaskInSessionStorage = (
  taskId: number,
  exportType: ExportType,
  updates: {
    status?: ExportStatus
    download_url?: string | null
  },
) => {
  const jsonSessionStorage = sessionStorage.getItem(EXPORT_TASKS)
  if (!jsonSessionStorage) return

  try {
    const res: Record<string, ExportTask[]> = JSON.parse(jsonSessionStorage)

    if (res[exportType]) {
      const taskIndex = res[exportType].findIndex((task) => task.id === taskId)

      if (taskIndex !== -1) {
        res[exportType][taskIndex] = {
          ...res[exportType][taskIndex],
          ...updates,
        }
        sessionStorage.setItem(EXPORT_TASKS, JSON.stringify(res))
      }
    }
  } catch {
    // Handle error silently
  }
}

const PendingTaskPoller = ({
  taskId,
  exportType: _exportType,
  onStatusChange,
}: {
  taskId: number
  exportType: ExportType
  onStatusChange: (
    taskId: number,
    status: ExportStatus,
    downloadUrl?: string | null,
  ) => void
}) => {
  useQueryExportReportById({
    exportReportId: taskId,
    options: {
      refetchInterval: 2000,
      enabled: true,
      onSuccess: (response) => {
        const { status, download_url } = response.data
        onStatusChange(taskId, status, download_url || null)
      },
    },
  })

  return null
}

const useGlobalExportReportsPollingLogic = () => {
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const [allTasks, setAllTasks] = useState<ExportTask[]>([])
  const [currentPollingTaskId, setCurrentPollingTaskId] = useState<
    number | null
  >(null)
  const [processedTaskIds, setProcessedTaskIds] = useState<Set<number>>(
    new Set(),
  )
  const shownSnackbarTaskIds = useRef<Set<number>>(new Set())
  const [exportReportIdToDownload, setExportReportIdToDownload] = useState<
    number | null
  >(null)

  useQueryExportReportDownload({
    exportReportId: exportReportIdToDownload ?? 0,
    options: {
      enabled: exportReportIdToDownload !== null,
      onSuccess: (response) => {
        const url = response.data.download_url
        if (url) {
          window.open(url, "_blank", "noopener,noreferrer")
        }
        setExportReportIdToDownload(null)
      },
    },
  })

  const refreshTasks = useCallback(() => {
    setAllTasks(getAllExportTasks())
  }, [])

  // Initial load - mark already-completed tasks so we don't re-show notifications on page reload
  useEffect(() => {
    const initialTasks = getAllExportTasks()
    initialTasks.forEach((task) => {
      if (task.status === ExportStatus.SUCCESS) {
        shownSnackbarTaskIds.current.add(task.id)
      }
    })
    setAllTasks(initialTasks)
  }, [])

  useEffect(() => {
    const handleStorageChange = () => {
      refreshTasks()
    }

    window.addEventListener("exportTaskAdded", handleStorageChange)

    return () => {
      window.removeEventListener("exportTaskAdded", handleStorageChange)
    }
  }, [refreshTasks])

  useEffect(() => {
    allTasks.forEach((task) => {
      if (
        task.status === ExportStatus.SUCCESS &&
        !shownSnackbarTaskIds.current.has(task.id)
      ) {
        shownSnackbarTaskIds.current.add(task.id)

        const title = t("allTransactions.fileIsReady", {
          filename: task.filename,
        })

        enqueueSnackbar(title, {
          variant: "success",
          autoHideDuration: null,
          anchorOrigin: {
            vertical: "top",
            horizontal: "center",
          },
          content: (key) => (
            <Snackbar
              id={key}
              variant="success"
              message={title}
              customContent={
                <ExportReadySnackbarContent
                  title={title}
                  onDownloadClick={() => setExportReportIdToDownload(task.id)}
                  snackbarId={key}
                />
              }
            />
          ),
        })
      }
    })
  }, [allTasks, enqueueSnackbar, t])

  // Find the first PENDING task that should be polled
  useEffect(() => {
    const pendingTasks = allTasks.filter(
      (task) =>
        task.status === ExportStatus.PENDING && !processedTaskIds.has(task.id),
    )

    if (pendingTasks.length === 0) {
      setCurrentPollingTaskId(null)
      return
    }

    // If no task is currently being polled, start with the first one
    if (currentPollingTaskId === null) {
      setCurrentPollingTaskId(pendingTasks[0].id)
      return
    }

    // Check if current task is still PENDING
    const currentTask = allTasks.find((t) => t.id === currentPollingTaskId)
    if (currentTask?.status !== ExportStatus.PENDING) {
      setProcessedTaskIds((prev) => new Set(prev).add(currentPollingTaskId!))
      const currentIndex = pendingTasks.findIndex(
        (t) => t.id === currentPollingTaskId,
      )
      const nextTask = pendingTasks[currentIndex + 1] || pendingTasks[0]
      setCurrentPollingTaskId(nextTask?.id || null)
    }
  }, [allTasks, currentPollingTaskId, processedTaskIds])

  const handleStatusChange = (
    taskId: number,
    status: ExportStatus,
    downloadUrl?: string | null,
  ) => {
    const task = allTasks.find((t) => t.id === taskId)
    if (!task) return

    updateTaskInSessionStorage(taskId, task.export_type, {
      status,
      download_url: downloadUrl,
    })

    window.dispatchEvent(new CustomEvent("exportTaskAdded"))
    refreshTasks()
  }

  const currentTask = useMemo(() => {
    if (!currentPollingTaskId) return null
    return allTasks.find((t) => t.id === currentPollingTaskId) || null
  }, [allTasks, currentPollingTaskId])

  return {
    currentPollingTaskId,
    currentTask,
    handleStatusChange,
  }
}

export const GlobalExportReportsPolling: FC = () => {
  const { currentTask, handleStatusChange } =
    useGlobalExportReportsPollingLogic()

  if (!currentTask) return null

  return (
    <PendingTaskPoller
      key={currentTask.id}
      taskId={currentTask.id}
      exportType={currentTask.export_type}
      onStatusChange={handleStatusChange}
    />
  )
}
